'use client';

import { useCallback, useSyncExternalStore } from 'react';

const CHANGE_EVENT = 'loristry:dismissed-changed';

/**
 * localStorage-backed dismissal state for banners and similar
 * "shown-until-user-dismisses" surfaces.
 *
 * Stays in sync with same-tab dismissals (custom event dispatched by
 * `dismiss()`) and cross-tab dismissals (`storage` event). `getSnapshot` and
 * `setItem` are both guarded — property access on `window.localStorage` can
 * throw `SecurityError` under restricted storage policy (sandboxed iframes
 * without allow-same-origin, some MDM / privacy modes), and `setItem` can
 * throw QuotaExceeded under Safari private mode. Both throw sites are
 * swallowed: an unreadable store reads as "not dismissed" (banner remains
 * visible), a failed write hides for this session only.
 *
 * Uses `useSyncExternalStore` so React manages the SSR handoff without a
 * hydration mismatch: `getServerSnapshot` returns `false` during server
 * render (matching the visible banner). Callers can render conditionally on
 * `dismissed` without a separate `mounted` guard.
 *
 * NOTE (Next.js dev only): dev mode does not reliably reconcile a divergent
 * server/client snapshot — a banner loaded already-dismissed can look
 * "stuck" visible under `npm run dev`. Production hydration reconciles
 * correctly (locked by promo-banner-client-hydration.test.tsx, which
 * exercises the same pattern).
 *
 * @param storageKey - Full localStorage key. Callers supply the whole key
 * (this hook does not add a prefix) so keys stay grep-friendly.
 * @returns Tuple of the current dismissed flag and a dismiss() function.
 */
export function useDismissed(storageKey: string): [boolean, () => void] {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      // Cross-tab: the storage event fires for every key, so only react to ours
      // (a null key means localStorage.clear(), which we still honor).
      const onStorage = (event: StorageEvent) => {
        if (event.key === null || event.key === storageKey) onStoreChange();
      };
      window.addEventListener('storage', onStorage);
      // Same-tab: dismiss() dispatches this since storage events don't fire in
      // the originating tab.
      window.addEventListener(CHANGE_EVENT, onStoreChange);
      return () => {
        window.removeEventListener('storage', onStorage);
        window.removeEventListener(CHANGE_EVENT, onStoreChange);
      };
    },
    [storageKey]
  );

  const getSnapshot = useCallback(() => {
    try {
      return window.localStorage.getItem(storageKey) === '1';
    } catch {
      // localStorage unavailable (e.g. privacy mode) — treat as not dismissed.
      return false;
    }
  }, [storageKey]);

  const getServerSnapshot = useCallback(() => false, []);

  const dismissed = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const dismiss = useCallback(() => {
    try {
      window.localStorage.setItem(storageKey, '1');
    } catch {
      // Persistence failed — acceptable; banner will re-show on next load.
    }
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, [storageKey]);

  return [dismissed, dismiss];
}
